import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ROLES } from "@/config/roles";
import { useAuth } from "@/hooks/use-auth";
import {
  Banknote,
  BarChart3,
  CalendarClock,
  Database,
  GraduationCap,
  LayoutGrid,
  PieChart,
  Settings,
  Table,
} from "lucide-react";
import { Link } from "react-router";
import { AnalyticsSection } from "./components/AnalyticsSection";
import { ConfigKonstantaDialog } from "./components/ConfigKonstantaDialog";
import { ConfigMultiplierIkuDialog } from "./components/ConfigMultiplierIkuDialog";
import { ConfigPersentaseDialog } from "./components/ConfigPersentaseDialog";
// Re-using the same interface structure
interface MenuItem {
  title: string;
  url: string;
  icon?: React.ElementType;
  allowedRoles: string[];
  description?: string;
}

export default function DasbhoardPage() {
  const { user } = useAuth();

  // Definisikan Menu Berdasarkan Role (mirip di AppSidebar tapi disesuaikan untuk Card layout)
  const mainMenus: MenuItem[] = [
    {
      title: "Remun Dosen",
      url: "/admin/remun-dosen",
      icon: Banknote,
      description: "Manajemen remunerasi dosen",
      allowedRoles: [
        ROLES.ADMIN_SUPER,
        ROLES.ADMIN_UNIV,
        ROLES.ADMIN_KEPEGAWAIAN,
        ROLES.ADMIN_HTL,
        ROLES.ADMIN_FAKULTAS,
      ],
    },
    {
      title: "Dashboard Dosen",
      url: "/dosen/dashboard",
      icon: GraduationCap,
      description: "Halaman utama dosen",
      allowedRoles: [ROLES.DOSEN],
    },
    {
      title: "Remunerasi Saya",
      url: "/dosen/remunerasi",
      icon: Banknote,
      description: "Lihat remunerasi Anda",
      allowedRoles: [ROLES.DOSEN],
    },
  ];

  const periodeMenus: MenuItem[] = [
    {
      title: "Komponen",
      url: "/admin/periode/komponen",
      icon: CalendarClock,
      description: "Kelola komponen periode",
      allowedRoles: [ROLES.ADMIN_SUPER],
    },
    {
      title: "Grade",
      url: "/admin/periode/grade",
      icon: CalendarClock,
      description: "Kelola grade periode",
      allowedRoles: [ROLES.ADMIN_SUPER],
    },
    {
      title: "Nilai Max/Unsur",
      url: "/admin/periode/nilai-max-unsur",
      icon: CalendarClock,
      description: "Kelola nilai max/unsur",
      allowedRoles: [ROLES.ADMIN_SUPER],
    },
    {
      title: "Cluster",
      url: "/admin/periode/cluster",
      icon: CalendarClock,
      description: "Kelola cluster",
      allowedRoles: [ROLES.ADMIN_SUPER],
    },
    {
      title: "Besaran Serdos",
      url: "/admin/periode/besaran-serdos",
      icon: CalendarClock,
      description: "Manajemen besaran serdos dosen",
      allowedRoles: [ROLES.ADMIN_SUPER],
    },
  ];

  const masterDataMenus: MenuItem[] = [
    {
      title: "Data Dosen",
      url: "/admin/master/dosen",
      icon: Database,
      description: "Master data dosen",
      allowedRoles: [ROLES.ADMIN_SUPER],
    },
    {
      title: "Komponen Kegiatan",
      url: "/admin/master/komponen-kegiatan",
      icon: Database,
      description: "Master data komponen kegiatan",
      allowedRoles: [ROLES.ADMIN_SUPER],
    },
    {
      title: "Unit Kerja",
      url: "/admin/master/unit-kerja",
      icon: Database,
      description: "Master data unit kerja",
      allowedRoles: [ROLES.ADMIN_SUPER],
    },
    {
      title: "Grade",
      url: "/admin/master/grade",
      icon: Database,
      description: "Master data grade",
      allowedRoles: [ROLES.ADMIN_SUPER],
    },
  ];

  const settingsMenus: MenuItem[] = [
    {
      title: "Konfigurasi CAS",
      url: "/settings/cas",
      icon: Settings,
      description: "Pengaturan integrasi CAS",
      allowedRoles: [ROLES.ADMIN_SUPER],
    },
    {
      title: "Log Aktivitas",
      url: "/settings/logs",
      icon: Settings,
      description: "Lihat log aktivitas sistem",
      allowedRoles: [ROLES.ADMIN_SUPER],
    },
  ];

  // Fungsi Filter Berdasarkan Role User
  const filterByRole = (menuList: MenuItem[]) => {
    return menuList.filter((menu) =>
      menu.allowedRoles.includes(user?.role || ""),
    );
  };

  const filteredMain = filterByRole(mainMenus);
  const filteredPeriode = filterByRole(periodeMenus);
  const filteredMaster = filterByRole(masterDataMenus);
  const filteredSettings = filterByRole(settingsMenus);

  const renderMenuSection = (title: string, menus: MenuItem[]) => {
    if (menus.length === 0) return null;

    return (
      <div className="space-y-3">
        <h2 className="text-sm font-bold tracking-wider uppercase text-muted-foreground">
          {title}
        </h2>
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
          {menus.map((menu) => (
            <Link
              key={menu.url}
              to={menu.url}
              className="flex items-start p-3 bg-card border border-muted hover:border-primary rounded-xl transition-all hover:shadow-sm group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mr-3 group-hover:bg-primary group-hover:text-primary-foreground transition-colors text-primary shrink-0">
                {menu.icon && <menu.icon className="h-5 w-5" />}
              </div>
              <div className="flex flex-col text-left">
                <span className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">
                  {menu.title}
                </span>
                <span className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  {menu.description}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 space-y-8 p-4 md:p-8 pt-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <div className="flex flex-wrap items-center md:justify-end gap-2">
          {/* <ProtectRole allowedRoles={[]}> */}
          <>
            <ConfigPersentaseDialog />
            <ConfigKonstantaDialog />
            <ConfigMultiplierIkuDialog />
          </>
          {/* </ProtectRole> */}
          <Button variant="outline" className="font-semibold shadow-sm" asChild>
            <Link to="/admin/dashboard/sebaran-sks">
              <PieChart className="w-4 h-4 mr-2" />
              Clusterisasi
            </Link>
          </Button>
          <Button variant="outline" className="font-semibold shadow-sm" asChild>
            <Link to="/admin/dashboard/cluster-iku">
              <BarChart3 className="w-4 h-4 mr-2" />
              Cluster IKU
            </Link>
          </Button>
          <Button variant="outline" className="font-semibold shadow-sm" asChild>
            <Link to="/admin/dashboard/analytics-70-table">
              <Table className="w-4 h-4 mr-2" />
              Komparasi Remun 70% Lama
            </Link>
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="font-semibold shadow-sm">
                <LayoutGrid className="w-4 h-4 mr-2" />
                Menu Navigasi
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] sm:max-w-3xl lg:max-w-5xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold mb-2">
                  Menu Navigasi
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6 pb-6">
                {renderMenuSection("Menu Utama", filteredMain)}
                {renderMenuSection("Konfigurasi Remunerasi", filteredPeriode)}
                {renderMenuSection("Data Master", filteredMaster)}
                {renderMenuSection("Sistem", filteredSettings)}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {user?.role !== ROLES.DOSEN && (
        <div className="mb-8">
          <AnalyticsSection />
        </div>
      )}
    </div>
  );
}
